/**
 * Stock movements that are not sales or purchases: transfers between
 * stores (vans loaded and unloaded) and stock counts. Two lists on one
 * screen, because a storekeeper reconciling a van needs both.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Skeleton, Stack, Tab, Tabs, Typography, useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'

import { fetchAdjustments, fetchStores, fetchTransfers } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import ReportShell, { rangeLabel } from '../reports/ReportShell'
import ReportToolbar from '../reports/ReportToolbar'
import { useDateRange } from '../reports/useDateRange'
import Keycap from '../components/Keycap'
import { downloadCsv } from '../reports/csv'
import { gregorian } from '../reports/hijri'
import { c, fmtMoney } from '../theme'
import { StoreSelect } from './bits'

type Kind = 'transfers' | 'counts'

export default function TransfersPage({ kind: initial = 'transfers' }: { kind?: Kind }) {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const { can } = useAuth()
  const narrow = useMediaQuery('(max-width:760px)')
  const { range, setRange, presets } = useDateRange()
  const [kind, setKind] = useState<Kind>(initial)
  const [store, setStore] = useState<number | ''>('')
  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const params = { from_date: range.from, to_date: range.to, store_id: store || undefined }
  const tq = useQuery({ queryKey: ['inv-transfers', params], queryFn: () => fetchTransfers(params),
                        enabled: kind === 'transfers' })
  const aq = useQuery({ queryKey: ['inv-adjustments', params],
                        queryFn: () => fetchAdjustments(params), enabled: kind === 'counts' })

  const newPath = kind === 'transfers' ? '/inventory/transfers/new' : '/inventory/counts/new'
  const mayNew = kind === 'transfers' ? can(P.inventoryTransfer) : can(P.inventoryAdjust)
  useHotkeys('inv-moves', [
    { combo: 'alt+n', label: t(kind === 'transfers' ? 'stock.newTransfer' : 'stock.newCount'),
      handler: () => nav(newPath) },
  ], mayNew)

  const q = kind === 'transfers' ? tq : aq
  const transfers = tq.data?.transfers ?? []
  const counts = aq.data?.adjustments ?? []
  const empty = kind === 'transfers' ? transfers.length === 0 : counts.length === 0
  const struck = (s: string) => ({ opacity: s === 'CANCELLED' ? 0.5 : 1,
    textDecoration: s === 'CANCELLED' ? 'line-through' : undefined })

  return (
    <ReportShell titleKey="nav.stockMoves" signatures={false}
      period={rangeLabel(range.from, range.to, i18n.language, t)}
      headRight={mayNew ? (
        <Button variant="contained" startIcon={<AddIcon />} className="no-print"
          onClick={() => nav(newPath)}>
          {t(kind === 'transfers' ? 'stock.newTransfer' : 'stock.newCount')}
          <Box component="span" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
            <Keycap combo="alt+n" /></Box>
        </Button>
      ) : undefined}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
          onExport={() => kind === 'transfers'
            ? downloadCsv(`transfers-${range.from}-${range.to}`, [
              ['Date', 'Number', 'From', 'To', 'Lines', 'Value', 'Status', 'Notes'],
              ...transfers.map((r) => [r.doc_date, r.doc_no, r.from_store_name, r.to_store_name,
                String(r.line_count), r.value ?? '', r.status, r.notes ?? ''])])
            : downloadCsv(`stock-counts-${range.from}-${range.to}`, [
              ['Date', 'Number', 'Store', 'Reason', 'Lines', 'Short', 'Excess', 'Value', 'Status'],
              ...counts.map((r) => [r.doc_date, r.doc_no, r.store_name, r.reason,
                String(r.line_count), String(r.short_lines), String(r.excess_lines),
                r.value ?? '', r.status])])}>
          <Tabs value={kind} onChange={(_, v) => setKind(v as Kind)}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}>
            <Tab value="transfers" label={t('stock.transfers')} />
            <Tab value="counts" label={t('stock.counts')} />
          </Tabs>
          <StoreSelect label={t('inv.store')} value={store} stores={stores.data ?? []}
            onChange={setStore} allowAll sx={{ minWidth: 200 }} />
        </ReportToolbar>
      }>

      {q.isError ? (
        <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
      ) : q.isLoading ? (
        <Box sx={{ p: 2 }}><Skeleton /><Skeleton /></Box>
      ) : empty ? (
        <Typography sx={{ p: 3, color: c.inkMuted }}>
          {t(kind === 'transfers' ? 'stock.noTransfers' : 'stock.noCounts')}</Typography>
      ) : kind === 'transfers' ? (
        narrow ? (
          <Box className="ruled">
            {transfers.map((r) => (
              <Row key={r.id} onClick={() => nav(`/inventory/transfers/${r.id}`)} style={struck(r.status)}
                title={`${r.from_store_name} → ${r.to_store_name}`}
                sub={<><span className="doc-no">{r.doc_no}</span> · {gregorian(r.doc_date, i18n.language)}</>}
                right={t('stock.nLines', { n: r.line_count })} />
            ))}
          </Box>
        ) : (
          <Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
            <table className="rpt" style={{ minWidth: 760 }}>
              <thead><tr>
                <th style={{ width: 104 }}>{t('entry.date')}</th>
                <th style={{ width: 140 }}>{t('report.voucherNo')}</th>
                <th>{t('stock.from')}</th>
                <th>{t('stock.to')}</th>
                <th className="num" style={{ width: 80 }}>{t('inv.lines')}</th>
                {transfers[0]?.value !== undefined && <th className="num" style={{ width: 120 }}>{t('inv.value')}</th>}
                <th style={{ width: 120 }}>{t('stock.enteredBy')}</th>
              </tr></thead>
              <tbody>
                {transfers.map((r) => (
                  <tr key={r.id} className="clickable" style={struck(r.status)}
                    onClick={() => nav(`/inventory/transfers/${r.id}`)}>
                    <td style={{ whiteSpace: 'nowrap' }}>{gregorian(r.doc_date, i18n.language)}</td>
                    <td><span className="doc-no" style={{ color: 'var(--primary-ink)' }}>{r.doc_no}</span></td>
                    <td>{r.from_store_name}{r.from_store_kind === 'VAN' && <VanChip />}</td>
                    <td>{r.to_store_name}{r.to_store_kind === 'VAN' && <VanChip />}</td>
                    <td className="num">{r.line_count}</td>
                    {r.value !== undefined && <td className="num">{fmtMoney(r.value)}</td>}
                    <td style={{ color: 'var(--ink-muted)' }}>{r.entered_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )
      ) : narrow ? (
        <Box className="ruled">
          {counts.map((r) => (
            <Row key={r.id} onClick={() => nav(`/inventory/counts/${r.id}`)} style={struck(r.status)}
              title={r.store_name}
              sub={<><span className="doc-no">{r.doc_no}</span> · {gregorian(r.doc_date, i18n.language)} · {t(`stock.reason${r.reason}`)}</>}
              right={r.value !== undefined ? fmtMoney(r.value) : t('stock.nLines', { n: r.line_count })} />
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
          <table className="rpt" style={{ minWidth: 760 }}>
            <thead><tr>
              <th style={{ width: 104 }}>{t('entry.date')}</th>
              <th style={{ width: 140 }}>{t('report.voucherNo')}</th>
              <th>{t('inv.store')}</th>
              <th style={{ width: 130 }}>{t('inv.reason')}</th>
              <th className="num" style={{ width: 90 }}>{t('stock.short')}</th>
              <th className="num" style={{ width: 90 }}>{t('stock.excess')}</th>
              {counts[0]?.value !== undefined && <th className="num" style={{ width: 120 }}>{t('inv.value')}</th>}
            </tr></thead>
            <tbody>
              {counts.map((r) => (
                <tr key={r.id} className="clickable" style={struck(r.status)}
                  onClick={() => nav(`/inventory/counts/${r.id}`)}>
                  <td style={{ whiteSpace: 'nowrap' }}>{gregorian(r.doc_date, i18n.language)}</td>
                  <td><span className="doc-no" style={{ color: 'var(--primary-ink)' }}>{r.doc_no}</span></td>
                  <td>{r.store_name}</td>
                  <td>{t(`stock.reason${r.reason}`)}</td>
                  <td className="num">{r.short_lines || ''}</td>
                  <td className="num">{r.excess_lines || ''}</td>
                  {r.value !== undefined && (
                    <td className="num" style={{ color: Number(r.value) < 0 ? c.dangerInk : undefined }}>
                      {fmtMoney(r.value)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
      <Stack sx={{ px: 2.5, pb: 2 }}>
        <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
          {t(kind === 'transfers' ? 'stock.transferNote' : 'stock.countNote')}</Typography>
      </Stack>
    </ReportShell>
  )
}

function VanChip() {
  const { t } = useTranslation()
  return <Chip size="small" label={t('inv.kindVAN')} sx={{ ml: 1, height: 18, fontSize: 10.5 }} />
}

function Row({ title, sub, right, onClick, style }: {
  title: string; sub: React.ReactNode; right: string; onClick: () => void
  style?: React.CSSProperties
}) {
  return (
    <Box component="button" type="button" onClick={onClick} style={style}
      sx={{ display: 'flex', width: '100%', gap: 1.5, px: 2, py: 1.25, border: 0,
            bgcolor: 'transparent', font: 'inherit', color: 'inherit', textAlign: 'start',
            cursor: 'pointer' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap dir="auto" sx={{ fontWeight: 500 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{sub}</Typography>
      </Box>
      <Typography className="num" sx={{ fontWeight: 600 }}>{right}</Typography>
    </Box>
  )
}
